import { useMemo, useState } from "react";
import PageLayout from "@/layouts/PageLayout";
import { useTranslation } from "react-i18next";

import { useGetOrgaInfo } from "@/hooks/api/organisation/useGetOrgaInfo";
import { useGetPlans } from "@/hooks/api/subscription/useGetPlans";
import { useSubscriptionStatus } from "@/hooks/api/subscription/useSubscriptionStatus";
import Button from "@/components/atoms/Button";
import SubscriptionEnterprise from "@/components/molecules/subscription/SubscriptionEnterprise";
import SubscriptionItem from "@/components/molecules/subscription/SubscriptionItem";

const PlansBilling = () => {
  const { t } = useTranslation();
  const { data: plans, isLoading: isLoadingPlans } = useGetPlans();
  const { data: orga } = useGetOrgaInfo();

  const [paymentInterval, setPaymentInterval] = useState<"month" | "year">("month");

  const activeSubscription = useMemo(() => orga?.plan, [orga]);

  const sortedPlans = useMemo(() => {
    if (!plans) return [];
    return plans.slice().sort((a, b) => {
      const aMonthly = a.prices.find((price) => price.interval === "month")?.unit_amount ?? 0;
      const bMonthly = b.prices.find((price) => price.interval === "month")?.unit_amount ?? 0;

      return aMonthly - bMonthly;
    });
  }, [plans]);

  const { data: plannedDowngrade, isLoading: isCheckingStatus } = useSubscriptionStatus(true);

  const perkNames = [
    t("settings.plans.perks.resultsPerSearch"),
    t("settings.plans.perks.entriesPerList"),
    t("settings.plans.perks.entriesPerAutofillList"),
    t("settings.plans.perks.numberOfLists"),
  ];

  const unlimited = t("settings.plans.unlimited");
  const limits: (string | React.ReactNode)[][] = [
    ["25", "25", "25", unlimited],
    ["100", "100", "100", unlimited],
    ["500", "500", "500", unlimited],
  ];

  return (
    <PageLayout>
      <div className={"mb-8"}>
        <h2 className={"text-2xl font-semibold text-primary"}>{t("settings.plans.title")}</h2>
        <p className={"max-w-lg text-sm text-quaternary"}>{t("settings.plans.description")}</p>
      </div>

      <div className={"flex items-end justify-between gap-4 pb-3"}>
        <div>
          <h3 className={"text-xl font-semibold text-primary"}>{t("settings.plans.yours")}</h3>
          <p className={"max-w-md text-sm text-quaternary"}>{t("settings.plans.yoursDescription")}</p>
        </div>
        <div>
          <div className={"flex gap-2 rounded-lg p-1.5 shadow-sm"}>
            <Button
              label={t("settings.plans.monthly")}
              size={"xs"}
              color={paymentInterval === "month" ? "brand" : "natural"}
              variants={paymentInterval === "month" ? "secondary" : "text"}
              className={"!rounded !px-5 !py-1"}
              onClick={() => {
                setPaymentInterval("month");
              }}
            />
            <Button
              label={t("settings.plans.annually")}
              size={"xs"}
              color={paymentInterval === "year" ? "brand" : "natural"}
              variants={paymentInterval === "year" ? "secondary" : "text"}
              className={"!rounded !px-5 !py-1"}
              onClick={() => {
                setPaymentInterval("year");
              }}
            />
          </div>
        </div>
      </div>

      <div className={"pb-16"}>
        <div className={"grid gap-8 pb-8 lg:grid-cols-2 xl:grid-cols-3"}>
          {isLoadingPlans || !orga || isCheckingStatus
            ? Array.from({ length: 3 }, (_, i) => (
                <div
                  key={i}
                  className="flex animate-pulse flex-col justify-center gap-3 rounded-2xl border border-secondary px-7 pb-10 pt-11"
                >
                  <div className={"h-5 w-16 rounded-full bg-gray-300"} />
                  <div className={"h-6 w-32 rounded-full bg-gray-300"} />
                  <div className="mt-5 flex flex-col gap-3.5 border-t border-t-secondary pb-8 pt-4">
                    <div className={"h-4 w-44 rounded-full bg-gray-300"} />
                    <div className={"h-4 w-44 rounded-full bg-gray-300"} />
                    <div className={"h-4 w-44 rounded-full bg-gray-300"} />
                    <div className={"h-4 w-44 rounded-full bg-gray-300"} />
                  </div>

                  <div className={"flex w-full items-center justify-center rounded-lg bg-gray-400 px-6 py-2.5"}>
                    <div className={"h-4 w-32 rounded-full bg-gray-300"} />
                  </div>
                </div>
              ))
            : sortedPlans.map((subscription, index) => (
                <SubscriptionItem
                  key={subscription.id}
                  subscription={subscription}
                  paymentType={paymentInterval}
                  activeSubscription={activeSubscription!}
                  plannedDowngrade={plannedDowngrade!}
                  perks={perkNames.map((perk, perkIndex) => ({
                    text: perk,
                    feature: limits[index][perkIndex],
                  }))}
                />
              ))}
        </div>

        <SubscriptionEnterprise />
      </div>
    </PageLayout>
  );
};

export default PlansBilling;
